import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import gql from 'graphql-tag';
import i18nMessages from './SideMenuTranslations';
import { APP_BASENAME } from '../../../config';
import { accountIsGP, accountIsMCP, accountTypeIsClient, userHasMemberRole, userHasOwnerRole } from './SideMenuHelper';
import apollo from '../../apollo';
import { growlErrors } from '../../../utils/NotificationUtil';
import { useContext } from 'react';
import { NotificationContext } from '../../../components/GrowlNotifications';

const GET_DOCUMENT_URL_QUERY = gql`
  query getDocumentUrlQuery($accountId: ID!, $type: DocumentType!) {
    documentUrl(accountId: $accountId, type: $type) {
      url
    }
  }
`;

const DocumentsSideMenuBuilder = (accountId, accountType, userRole) => {
  const { formatMessage, locale } = useIntl();
  const history = useHistory();
  const growl = useContext(NotificationContext);

  const accountIsTopLevel = accountTypeIsClient(accountType);

  const accountIsNotClient = !accountTypeIsClient(accountType);

  const accountIsMcpType = accountIsMCP(accountType);
  const accountIsGpType = accountIsGP(accountType);

  const userIsOwner = userHasOwnerRole(userRole);
  const userIsNotMember = !userHasMemberRole(userRole);

  const getDocumentUrl = async (documentType) => {
    try {
      apollo
        .query({
          query: GET_DOCUMENT_URL_QUERY,
          variables: {
            accountId,
            type: documentType,
          },
        })
        .then((res) => {
          const documentUrl = res.data.documentUrl.url;

          window.open(documentUrl, '_blank', 'noreferrer');
        });
    } catch (error) {
      growlErrors(growl);
    }
  };

  const buildPartnerResourcesMenu = () => {
    const partnerResourcesItems = [];

    const cpResources = {
      label: formatMessage(i18nMessages.gdprWhenSellingAndServicing),
      command: () => {
        window.open(
          `${APP_BASENAME}/assets/documentation/CP-GDPR_when_selling_and_servicing_ver1_0.pdf`,
          'GDPR-when-selling-and-servicing',
          '_blank',
        );
      },
    };

    const mcpResources = {
      label: formatMessage(i18nMessages.supportProcedure),
      command: () => {
        getDocumentUrl('GR8PI_INTERNAL_SUPPORT_PROCEDURE');
      },
    };

    if (accountIsNotClient) {
      partnerResourcesItems.push(cpResources);
    }

    if (accountIsTopLevel) {
      partnerResourcesItems.push(mcpResources);
    }

    const partnerResources = {
      label: formatMessage(i18nMessages.partnerResources),
      items: partnerResourcesItems,
    };

    return partnerResources;
  };

  const buildDocumentationMenu = () => {
    const documentationItems = [];

    const partnerResources = buildPartnerResourcesMenu();

    const quickGuides = {
      label: formatMessage(i18nMessages.quickGuides),
      items: [
        {
          label: formatMessage(i18nMessages.ariQuickGuide),
          command: () => {
            window.open(
              `${APP_BASENAME}/assets/documentation/ari/${locale}/ARI_QuickGuide.pdf`,
              'ARI_QuickGuide',
              '_blank',
            );
          },
        },
      ],
    };

    documentationItems.push(quickGuides);

    if (accountIsNotClient && userIsNotMember) {
      documentationItems.push(partnerResources);
    }

    const documentation = {
      label: formatMessage(i18nMessages.documentation),
      icon: 'pi pi-info',
      items: documentationItems,
    };

    return documentation;
  };

  const buildImportantDocumentsMenu = () => {
    const importantDocumentsItems = [
      {
        label: formatMessage(i18nMessages.userTermsOfUse),
        command: () => {
          getDocumentUrl('USER_TERMS_OF_USE');
        },
      },
      {
        label: formatMessage(i18nMessages.privacyPolicy),
        command: () => {
          getDocumentUrl('PRIVACY_POLICY');
        },
      },
      {
        label: formatMessage(i18nMessages.serviceLevelAgreement),
        command: () => {
          getDocumentUrl('GR8PI_SERVICE_LEVEL_AGREEMENT');
        },
      },
    ];

    if (accountIsMcpType) {
      importantDocumentsItems.push({
        label: formatMessage(i18nMessages.termsOfServiceOnlinePurchases),
        command: () => {
          getDocumentUrl('GR8PI_TERMS_OF_SERVICE');
        },
      });
    }

    if (userIsOwner) {
      importantDocumentsItems.push({
        label: formatMessage(i18nMessages.dataProcessingAgreement),
        command: () => {
          getDocumentUrl('DATA_PROCESSING_AGREEMENT');
        },
      });
      importantDocumentsItems.push({
        label: formatMessage(i18nMessages.clientTermsOfUse),
        command: () => {
          getDocumentUrl('OWNER_TERMS_OF_USE');
        },
      });

      if (accountIsGpType) {
        importantDocumentsItems.push({
          label: formatMessage(i18nMessages.gpAgreement),
          command: () => {
            getDocumentUrl('GPI_PARTNER_AGREEMENT');
          },
        });
      }
    }

    const importantDocuments = {
      label: formatMessage(i18nMessages.importantDocuments),
      icon: 'pi pi-info-circle',
      items: importantDocumentsItems,
    };

    return importantDocuments;
  };

  const buildCommercialAgreementsMenu = () => {
    const addCommercialAgreement = {
      label: formatMessage(i18nMessages.addCommercialAgreement),
      icon: 'pi pi-plus-circle',
      command: () => {
        const url = `/accounts/${accountId}/commercial-agreements/new`;
        history.push(url);
      },
    };

    const listCommercialAgreements = {
      label: formatMessage(i18nMessages.listCommercialAgreements),
      icon: 'pi pi-list',
      command: () => {
        const url = `/accounts/${accountId}/commercial-agreements`;
        history.push(url);
      },
    };

    const commercialAgreementsItems = [];

    if (userIsOwner && accountIsNotClient) {
      commercialAgreementsItems.push(addCommercialAgreement);
    }

    if (userIsOwner) {
      commercialAgreementsItems.push(listCommercialAgreements);
    }

    const commercialAgreements = {
      label: formatMessage(i18nMessages.commercialAgreements),
      icon: 'pi pi-briefcase',
      items: commercialAgreementsItems,
    };

    return commercialAgreements;
  };

  const buildDocumentsMenu = () => {
    const documentItems = [];

    const documentation = buildDocumentationMenu();
    const importantDocuments = buildImportantDocumentsMenu();
    const commercialAgreements = buildCommercialAgreementsMenu();

    if (userIsOwner) {
      documentItems.push(documentation);
      documentItems.push(importantDocuments);
      documentItems.push(commercialAgreements);
    } else {
      documentItems.push(documentation);
      documentItems.push(importantDocuments);
    }

    const documents = {
      label: formatMessage(i18nMessages.documents),
      icon: 'pi pi-question-circle',
      items: documentItems,
    };

    return documents;
  };

  return {
    buildDocumentsMenu,
  };
};

export default DocumentsSideMenuBuilder;
